// Postcode + huisnummer → straat/woonplaats for the wizards' "Nieuwe relatie" form. Calls the api/
// proxy GET /api/relaties/postcode-lookup — NOT postcode.eu directly — which is the faithful port of
// the Jmix GratisPostcodeApiLookupService fallback chain (existing klant rows → local postcode table →
// postcode.eu). The access token is bearer-only (in memory), so this goes through an authenticated
// fetch like the other src/api clients (geocoding.ts precedent).
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** The resolved address fields; either may be null. */
export interface PostcodeLookupResult {
  straat: string | null;
  woonplaats: string | null;
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Resolve the street + city for a postcode and house number. Returns null when either input is blank,
 * nothing resolves (204), or the call fails — mirroring the Jmix "leave the fields as-is" contract.
 */
/** One Postcode.eu autocomplete match — `context` resolves to a full address via {@link addressDetails}. */
export interface AddressSuggestion {
  context: string;
  label: string;
  description: string | null;
  precision: string | null;
}

/** Autocomplete outcome: the matches, plus whether the server has no Postcode.eu credentials at all. */
export interface AddressAutocompleteResult {
  matches: AddressSuggestion[];
  unavailable: boolean;
}

/** A resolved autocomplete address (Postcode.eu international address details). */
export interface AddressDetails {
  street: string | null;
  postcode: string | null;
  city: string | null;
  buildingNumber: string | null;
  buildingNumberAddition: string | null;
}

/**
 * Postcode.eu address autocomplete for the single "Zoek op adres" field (the Jmix relation step's
 * address field). One `session` id per typing session — Postcode.eu bills per session, not per
 * keystroke. Returns [] on any failure so the field simply shows no suggestions.
 */
export async function autocompleteAddress(
  term: string,
  session: string,
  // Hierarchical: "nld" at country level, then the context of a picked non-Address match so the next
  // call returns that match's children (postcode → house numbers, etc.).
  context = "nld",
): Promise<AddressAutocompleteResult> {
  // Postcode.eu tokenises a Dutch postcode as "1234 AB"; typed glued together ("7581WL 7") it falls
  // back to matching just the numeric part and returns every 7581xx postcode. Split it back apart.
  const q = term.trim().replace(/\b(\d{4})\s*([A-Za-z]{2})\b/, "$1 $2");
  if (!q && context === "nld") return { matches: [], unavailable: false };
  const params = new URLSearchParams({ term: q, session, context });
  try {
    const res = await fetch(`${API_URL}/relaties/address-autocomplete?${params.toString()}`, {
      headers: authHeaders(),
    });
    // 503 = Postcode.eu isn't configured on this server. That is a setting, not an empty result, and
    // the difference has to reach the operator — otherwise a machine without credentials looks like a
    // broken address search.
    if (res.status === 503) return { matches: [], unavailable: true };
    if (!res.ok) return { matches: [], unavailable: false };
    return { matches: (await res.json()) as AddressSuggestion[], unavailable: false };
  } catch {
    return { matches: [], unavailable: false };
  }
}

/** Resolve a picked suggestion into street/postcode/city/house number. Null when it doesn't resolve. */
export async function addressDetails(context: string, session: string): Promise<AddressDetails | null> {
  const params = new URLSearchParams({ context, session });
  try {
    const res = await fetch(`${API_URL}/relaties/address-details?${params.toString()}`, {
      headers: authHeaders(),
    });
    if (res.status !== 200) return null;
    return (await res.json()) as AddressDetails;
  } catch {
    return null;
  }
}

export async function lookupPostcode(
  postcode: string,
  huisnummer: string,
  toevoeging?: string | null,
): Promise<PostcodeLookupResult | null> {
  const pc = postcode.trim();
  const nr = huisnummer.trim();
  if (!pc || !nr) return null;
  const params = new URLSearchParams({ postcode: pc, huisnummer: nr });
  const add = toevoeging?.trim();
  if (add) params.set("toevoeging", add);
  try {
    const res = await fetch(`${API_URL}/relaties/postcode-lookup?${params.toString()}`, {
      headers: authHeaders(),
    });
    if (res.status !== 200) return null;
    return (await res.json()) as PostcodeLookupResult;
  } catch {
    return null;
  }
}
